import anime from 'animejs';

const PREFERS_REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

export default class ChristmasCampaign {
	el: HTMLElement;
	stageElement?: HTMLElement;
	currentStage?: HTMLElement;

	heartClassName = 'christmas-campaign__heart christmas-campaign__random-heart';
	initialDelay = 500;
	delayBetweenLoops = 1300;
	swapFadeDuration = 380;
	fadeEasing = 'easeOutQuad';
	hearts = 20;
	loop = true;

	constructor(el: HTMLElement) {
		this.el = el;

		this.stageElement = <HTMLElement>el.querySelector('[data-christmas-stage]');

		if (!this.stageElement) {
			return;
		}

		if (document.readyState === 'complete') {
			this.play();
		} else {
			window.addEventListener('load', () => this.play(this.initialDelay));
		}
	}

	async play(initialDelay: number = 0) {
		const currentStage = this.stageElement;

		if (!currentStage) {
			return;
		}

		const staticHearts = <[HTMLElement]>(
			Array.from(currentStage.querySelectorAll('[data-static-heart]'))
		);

		const hearts = this.addHearts(currentStage);

		staticHearts.forEach((heart) => {
			const transform = getComputedStyle(heart).transform;
			const matrix = new DOMMatrixReadOnly(transform);
			const translateX = matrix.m41;
			const translateY = matrix.m42;
			anime.set(heart, {
				scale: 0.3,
				opacity: 0,
				translateX,
				translateY,
			});
		});
		anime.set(hearts, { opacity: 0 });

		await anime
			.timeline({
				delay: initialDelay,
			})
			.add(
				window.matchMedia(PREFERS_REDUCED_MOTION_MEDIA_QUERY).matches
					? {}
					: {
							targets: hearts,
							keyframes: [{ opacity: [0, 1] }, { opacity: [1, 0] }],
							scale: () => anime.random(5, 9),
							top: (el: HTMLElement) => {
								const currentTop = parseFloat(
									el.style.getPropertyValue('top').replace('%', '')
								);
								const targetTop =
									currentTop < 50 ? anime.random(60, 80) : anime.random(20, 40);
								return `${targetTop}%`;
							},
							left: (el: HTMLElement) => {
								const currentLeft = parseFloat(
									el.style.getPropertyValue('left').replace('%', '')
								);
								const targetLeft =
									currentLeft < 50
										? anime.random(60, 80)
										: anime.random(20, 40);
								return `${targetLeft}%`;
							},
							easing: 'easeOutQuad',
							duration: 1500,
							delay: anime.stagger(`+${anime.random(20, 180)}`),
						}
			)
			.add(
				{
					targets: staticHearts,
					opacity: 1,
					scale: 1,
					easing: 'easeOutQuad',
					duration: 1000,
					delay: anime.stagger(150),
				},
				1500
			).finished;

		for (const heart of hearts) {
			if (heart.parentElement) {
				heart.parentElement.removeChild(heart);
			}
		}

		if (!this.loop) {
			return;
		}

		await anime.timeline({}).add({}, this.delayBetweenLoops).finished;

		await anime({
			targets: staticHearts,
			opacity: 0,
			scale: 2,
			duration: 600,
			delay: anime.stagger(150),
			easing: 'easeInQuad',
		}).finished;

		staticHearts.forEach((heart) => {
			heart.style = '';
		});

		this.play();
	}

	addHearts(stageElement: HTMLElement): HTMLElement[] {
		const frag = document.createDocumentFragment();

		const hearts = [...Array(this.hearts)].map(() => {
			const heart = document.createElement('div');
			heart.className = this.heartClassName;
			heart.role = 'presentation';
			heart.style.setProperty(
				'--heart-size',
				`clamp(1rem, ${anime.random(200, 500) / 100}%, 15%)`
			);
			heart.style.setProperty('top', `${anime.random(45, 55)}%`);
			heart.style.setProperty('left', `${anime.random(45, 55)}%`);

			frag.appendChild(heart);

			return heart;
		});

		stageElement.appendChild(frag);

		return hearts;
	}
}
